import React, { useEffect, useRef, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Grid, Typography } from "@mui/material";
import {
  ArrowLeftRightIcon,
  BellIcon,
  BlocksIcon,
  Building2Icon,
  CalculatorIcon,
  GiftIcon,
  LifeBuoyIcon,
  TicketIcon,
  HistoryIcon,
  LockIcon,
  MapPinIcon,
  ContrastIcon,
  PlugIcon,
  ReceiptIcon,
  ShieldCheckIcon,
  SlidersHorizontalIcon,
  ToggleLeftIcon,
  UserIcon,
  UsersIcon,
} from "lucide-react";
import { ROUTES, INTERNAL_ACCOUNT_ROUTES, SINGLE_SEAT_HIDDEN_ROUTES } from "routes";
import { NARROW_COLUMN_WIDTH } from "config";

import { Tabs, TabPanel, SideNav } from "@repo/ui";
import {
  Header,
  useHasReferrerBack,
  settingsNavIconMotionSx,
  selectedNavIconSx,
  NAV_ROW_MENU_ITEM,
} from "components";
import { InfoTooltip } from "components/feedback/InfoTooltip";
import { useCanAccess, useIsInternalUser } from "hooks";
import { useCompanySeats, useReferralsEnabled } from "hooks/api";
import { Permissions } from "types";

import {
  AppearancePage,
  ControlsPage,
  FieldsPage,
  BusinessFiguresPage,
  ActivityLogPage,
  PersonalActivityPage,
  AddressBookPage,
  FeaturesPage,
  BillingPage,
  CompanyGeneralPage,
  InstallationPage,
  IntegrationsPage,
  NotificationsPage,
  ProfilePage,
  SecurityPage,
  RolesPage,
  UsersPage,
} from "pages/settings";

// TODO: TBD move this to settings?
import { ReferralsPage } from "pages/main";
// Direct path rather than the `pages/support` barrel: that barrel also re-exports the docs
// reader, whose markdown renderer is code-split, and pulling it in here would drag the whole
// chunk into the settings bundle.
import { SupportPage } from "pages/support/SupportPage";
// Publishes `settingsHeaderH` to the section components so one that wants its own sticky
// header pins BELOW the shell's, not underneath it. Own module to avoid an import cycle —
// this file imports every section component.
import { SettingsStickyOffsetContext } from "./settingsStickyOffset";

/** Which of the two menus a pathname belongs to. */
type SettingsScope = "personal" | "company";

interface SettingsPageProps {
  /**
   * The scope this ROUTE declares, used only for the two landing routes.
   *
   * Since VRG-221 flattened the leaves, `/settings/controls` no longer says which tab
   * it belongs to, so the scope is derived from menu membership (`scopeForPathname`)
   * rather than parsed out of the path. This prop is the fallback for the paths that
   * have no section to match — `/settings`, `/settings/personal`, `/settings/company`
   * — which are exactly the ones App.tsx can state authoritatively.
   *
   * It was previously accepted and then ignored entirely; the tab came from the URL
   * containing the word "company".
   */
  tab?: SettingsScope;
}

/**
 * Leading nav glyphs. The bar each one has to clear: someone who has never seen
 * the page could NAME the icon unprompted and not be surprised by where it goes.
 *
 * The two "General" entries are the only hard ones, and they are deliberately NOT
 * a gear. Settings is already a gear in the main app sidebar, so a gear inside it
 * is a gear inside a gear — the icon would carry no information at the only level
 * where it has to. Instead each General takes the SUBJECT of its page, which is
 * exactly what "General" means in each context: Personal → General is you (photo,
 * name, contact, login), so a person; Company → General is the company itself
 * (name, logos, brand), so a building. "Person" and "building" are both nameable
 * on sight and neither is filler.
 *
 * (Personal → General's person glyph never sits beside Company → Users' people
 * glyph: they live in different tabs and are never rendered in one list.)
 */
// 24, not 18: the sidebar nav and the Help popover both render their glyphs at 24
// with strokeWidth 1 beside 16px Lato. This nav is the same kind of list and sat
// at 18, which read as a different, smaller menu language on the same screen.
// Measured before changing: sidebar 24x24, popover 24x24, this one 18x18.
const ICON_PROPS = { size: 24, strokeWidth: 1 } as const;


interface SettingsMenuItem {
  label: string;
  /** Leading glyph for the left nav. Required — a half-iconned nav reads as unfinished. */
  icon: React.ReactNode;
  route: string;
  visible: boolean;
  component: React.ReactNode;
  // Pages that render their own <Header title> (usually list pages with a
  // right-aligned action row) opt out of the central title to avoid a duplicate.
  selfTitled?: boolean;
  // The view permission that gates this section. When the current user (or an
  // in-space Vera staffer, capped to the brand's grant) lacks it, the left-nav
  // row is disabled with a tooltip naming the permission (§27). Undefined = always
  // available (e.g. Personal sections).
  //
  // An ARRAY means ANY of them opens the section, not all. `@Permissions(...)` on the
  // API is an AND, so this is deliberately the looser of the two and the backend stays
  // the real boundary (§27) — the array exists for a section whose halves are separately
  // gated and where holding EITHER half is enough to have somewhere to land. Fields is
  // the only one today: a company with no store connected has nothing to map but still
  // has defaults to set, so `field_default_view` alone must reach the page.
  permission?: Permissions | Permissions[];
  /**
   * A glossary entry id for the section's own subject, shown as an `(i)` beside the
   * section title (§44 — read from the glossary, never a sentence typed here).
   *
   * Only for a section whose NAME is a term of art the reader may not know. Most are
   * not: "Users", "Address book" and "Billing" describe themselves. Integrations is the
   * case that justified the field — the page it opens is a card grid with no heading of
   * its own, so the term has nowhere else on that surface to hang.
   */
  term?: string;
  /**
   * Cap this section's content to a readable column instead of letting it span
   * the full width.
   *
   * Opt-IN rather than a blanket wrapper on the layout, because the right answer
   * differs by section: forms and prose (Installation, Features, Address book,
   * General) gain nothing from 1900px — the descriptions run far past a
   * comfortable measure and the fields just stretch — while data tables
   * (Users, Billing invoices) and the permissions matrix (Roles) must NOT be
   * boxed in.
   *
   * ⚠️ THE ORIGINAL REASON GIVEN HERE WAS WRONG, and the correction matters because
   * it points at different work. This used to say tables "use the width
   * productively". Measured on the Certificates list (VRG-325), they do not: every
   * column is a fixed `width:` px with no `flex`, so the column layout is
   * byte-identical at 1280 and at 3440 and not one pixel of the surplus reaches a
   * column. At 1920, 463px sits as slack INSIDE columns and a further 375px as an
   * empty `.MuiDataGrid-filler` — 46.8% of the grid unused, while `scan_counter`
   * holds 120px for a single `0` glyph.
   *
   * The CONCLUSION survives: do not cap these to the form column. But the real
   * reason is that their fixed columns already total ~1415px, so an 1100px cap
   * forces horizontal scroll — capping `RolePage`'s grid was measured at 692px of
   * overflow, hiding email and actions. Right answer, wrong rationale.
   *
   * What the data actually implies is column-width rebalancing (or `flex` columns),
   * which is per-page work tracked as VRG-325. A global ceiling for the opposite
   * end — stopping a table sprawling across an ultrawide — lives in the shared
   * `Table` as `TABLE_MAX_WIDTH`.
   */
  narrow?: boolean;
}

// §27: short display names (§25 length cap) for each section's `permission`, named in
// the locked-row tooltip below rather than a bare "you don't have permission" — the
// copy has to say exactly what to ask an admin for.
const SECTION_PERMISSION_LABELS: Partial<Record<Permissions, string>> = {
  view_company: "View company",
  users_view: "View users",
  view_roles: "View roles",
  billing_view: "View billing",
  address_view: "View address book",
  installation_view: "View installation",
  integration_view: "View integrations",
  field_mapping_view: "View field mapping",
  field_default_view: "View field defaults",
  activity_log_view: "View activity log",
  // VRG-459 — the Business figures row gates on the analytics VIEW permission, not
  // on `analytics_input_edit` (which disables the fields in place instead, §27).
  view_analytics: "View analytics",
};

/**
 * Sticky offsets for this page's three stacked bars, DERIVED rather than hardcoded.
 *
 * They were the literals 64 / 117 / 129, which encoded the shared `Header`'s height at the time
 * they were written. When Header grew (it now reserves the wayfinding row's space and a 49px title
 * row so every page's title sits at the same height), the header became 93px while the tab bar was
 * still pinned at 64 — i.e. 29px INSIDE the header. At rest that was invisible, because the header's
 * bottom happens to land exactly where the tab bar starts; scrolled, the tab bar rode up over the
 * title.
 *
 * Measured live: header 93px, tab bar 53px. Naming them means the next header change moves all
 * three together instead of silently overlapping.
 */
// 64 — the sub-nav's flow position (88 absolute) MINUS the scroll container's 24px top padding.
//
// This is the third time this constant has gone stale and the reason it is now measured directly:
// arithmetic on the header's height keeps getting the reference frame wrong. A sticky `top` is
// resolved against the nearest scrolling ancestor's padding box, and which element that is (and
// what padding it has) changed when the header stopped reserving the wayfinding row.
//
// That subtraction is the whole bug, and I got it wrong twice by leaving it out. `top` on a sticky
// element is resolved against the nearest SCROLLING ancestor's padding box, and this page's scroller
// carries 24px of top padding — so `top: 88` does not mean "88 from the viewport", it means 112.
//
// Measured, with the nav's natural position identical (88) on both sections:
//
//   Appearance  not scrollable → sticky never engages → nav sits at its natural 88
//   Profile     scrollable (overflows by 136px) → sticky pushes it DOWN to 24 + 88 = 112
//
// A 24px difference between two sections of the same page, which is the "clicking Appearance moves
// the whole menu" report. The menu was not moving on SCROLL — it rendered in two different places
// depending on whether the section happened to be taller than the viewport. Note the direction:
// sticky pushed it DOWN, because the offset EXCEEDED the element's natural offset inside the padding
// box. That is the same failure the docs rails had, in the opposite direction.
//
// The invariant: pinned == flow, i.e. GUTTER + this == natural. Verify on a SCROLLABLE section, and
// compare a scrollable section against a non-scrollable one — a single non-scrollable section cannot
// show the bug, which is exactly how it survived the last two verification passes.
// 68.5, up from 64 (VRG-438). The old value was short by exactly the header's own -4.5px
// lift, which is why every settings section's action row started 4.5px INSIDE the sticky
// band above it — see the `marginBottom: 0` note on the Header below for the mechanism.
//
// MEASURED, not derived: with the double lift removed, the header's painted box runs
// [19.5, 92.5] and the scroll container's padding box starts at 24, so the space it
// occupies is 92.5 - 24 = 68.5. The invariant below (pinned == flow) then holds again:
// the sub-nav's sticky top resolves to 24 + 68.5 = 92.5, which is exactly where the
// content column now begins in flow.
const SETTINGS_HEADER_H = 68.5
// 0 while the F TRIAL hides the Personal/Company tab bar. Restore to 53 to go back to A.
const SETTINGS_TABBAR_H = 0
/** Below the header + tab bar — where the sub-nav and TabPanel content pin. */
const SETTINGS_BARS_H = SETTINGS_HEADER_H + SETTINGS_TABBAR_H
/**
 * The page's own gutter — Layout gives every page `p: 6` (24px), and a sticky `top` is measured from
 * that padding box, not from the viewport.
 */
const SETTINGS_PAGE_GUTTER = 24

/**
 * Where the sub-nav pins. Equal to the bars above it and NOT 12px lower.
 *
 * The `+ 12` here was "clearing the TabPanel's own top padding" — padding that is now 0, so the 12
 * became a pure offset between the box's PINNED position and its FLOW position, and that difference
 * is a visible jump: measured, the nav sat at 117 on a short page (flow, nothing to stick against)
 * and 129 on a tall one (pinned at 24 + 105). Clicking between Appearance and Profile moved the whole
 * nav 12px, which is what "the sidenav moves up" was.
 *
 * With the offset gone, pinned == flow == SETTINGS_PAGE_GUTTER + SETTINGS_BARS_H, so the nav cannot
 * move when a page happens to be scrollable.
 */
const SETTINGS_SUBNAV_TOP = SETTINGS_BARS_H

/**
 * How much TALLER the page header is when it carries a "← Back to <referrer>" link.
 *
 * Measured, not derived: the wayfinding row is 26px (its own `pt: 2` = 8, plus the link's 18px line
 * box) and the Stack gap below it is 12 — but the row also costs the negative margin the header
 * gives up when it stops lifting. 26 + 12 + 4.5 = 42.5.
 *
 * 42.5, down from 47 (VRG-438). The old figure counted 9px of given-up margin — `marginTop` AND
 * `marginBottom` at -4.5 each — which was itself the double-lift bug: only ONE of those two ever
 * belonged on this page, so only 4.5 is given up. With `marginBottom` now pinned to 0 in both
 * branches (see the Header's sx), the link's presence costs the row, the gap, and the marginTop.
 *
 * Every sticky `top` on this page is expressed as "below the header", so all of them shift by this
 * when a link is present. Getting it wrong does not merely misalign: the sub-nav's PINNED position
 * would stop matching its FLOW position, which is the jump-on-scroll bug the constants above were
 * introduced to kill (twice). Re-measure with `getBoundingClientRect` if the header changes.
 */
const SETTINGS_BACK_LINK_ROW_H = 42.5

// Exported (VRG-227) so the docs index can read the section LABEL order straight off
// these two arrays rather than a second, hand-typed copy that could drift — see
// `docs/categoryOrder.ts`. `DocPage`/`SettingsPage` are already both eagerly imported by
// `App.tsx` from the same barrel, so this adds no new bundle weight; it only exposes an
// array that already existed.
export const PERSONAL_MENU: SettingsMenuItem[] = [
  {
    // "Profile", not "General": the page IS you — photo, name, title, contact,
    // login. "General" said nothing, and left two unrelated pages sharing a label
    // across the two tabs.
    label: "Profile",
    icon: <UserIcon {...ICON_PROPS} />,
    route: ROUTES.SETTINGS_PERSONAL_PROFILE,
    visible: true,
    component: <ProfilePage />,
    narrow: true,
  },
  {
    // VRG-490 (D6) — right after Profile: Profile is who you are, Security is
    // how you prove it's you (password + two-factor). A lock, not the
    // ShieldCheckIcon Company → Roles & permissions already owns — different
    // menu, never rendered side by side, but a distinct glyph reads cleaner
    // and "lock" is the more literal match for "how you sign in".
    label: "Security",
    icon: <LockIcon {...ICON_PROPS} />,
    route: ROUTES.SETTINGS_PERSONAL_SECURITY,
    visible: true,
    component: <SecurityPage />,
    narrow: true,
  },
  {
    // Sits between Profile and Notifications: Profile is who you are, Appearance
    // is how the app looks to you, Notifications is what it sends you — narrowing
    // from the person outward.
    // A palette. Nameable on sight by anyone, and it means "how this looks",
    // which is exactly the page. Deliberately NOT a gear (Settings is already a
    // gear in the main sidebar, so a gear inside it carries no information) and
    // not a sun/moon, which would say "theme" and go stale the moment the page
    // holds anything but theme — which it already does.
    label: "Appearance",
    // Contrast, not a palette and not sun/moon. Palette is Studio's in the main
    // sidebar ("design your certificate page") and must not be reused. Sun/moon was
    // the first swap but it duplicates the glyphs INSIDE this page — the Theme
    // control's own options are Sun, Moon and Laptop — so the nav icon repeated its
    // own content. Contrast is the conventional display/appearance mark, nameable on
    // sight, and unused anywhere else in the app.
    icon: <ContrastIcon {...ICON_PROPS} />,
    route: ROUTES.SETTINGS_PERSONAL_APPEARANCE,
    visible: true,
    component: <AppearancePage />,
    // Same readable-measure cap as its siblings. Its panels hold prose descriptions and
    // a row of choices; full-viewport-wide they stretched to ~1500px while Profile and
    // Notifications beside them stopped at 1100, so moving between personal settings
    // pages changed the column width under you.
    narrow: true,
  },
  {
    label: "Notifications",
    icon: <BellIcon {...ICON_PROPS} />,
    route: ROUTES.SETTINGS_PERSONAL_NOTIFICATIONS,
    visible: true,
    component: <NotificationsPage />,
    narrow: true,
  },
  {
    // "Controls" rather than "Keyboard shortcuts": the page is the home for how
    // the app responds to you, and shortcuts are only its first occupant. Naming
    // a section after its first tenant is how you get a "Keyboard shortcuts" page
    // holding three things that aren't keyboard shortcuts. Matches Vergence's
    // /settings/controls. No permission — a per-device preference, like Theme.
    label: "Controls",
    // Sliders, not a keyboard: the page is named for what it will hold generally,
    // and its icon should age the same way.
    icon: <SlidersHorizontalIcon {...ICON_PROPS} />,
    route: ROUTES.SETTINGS_PERSONAL_CONTROLS,
    visible: true,
    component: <ControlsPage />,
    narrow: true,
  },
  {
    // Your own slice of the company Activity Log (Company → Activity log, below) —
    // pinned to your own actions rather than the whole company's (VRG-338). No
    // `permission` here, deliberately: reading your own activity needs no company-wide
    // grant, unlike the company page's `activity_log_view`. See PersonalActivityPage's
    // docblock for the one remaining constraint this can't paper over (the shared
    // endpoint is admin-only server-side).
    label: "Activity",
    // Same glyph as Company → Activity log (§21: one action, one icon, across surfaces).
    icon: <HistoryIcon {...ICON_PROPS} />,
    route: ROUTES.SETTINGS_PERSONAL_ACTIVITY,
    visible: true,
    component: <PersonalActivityPage />,
  },
  {
    // The requests YOU raised. Its company-wide twin lives under Company → Support, and the
    // pair replaces the two chip buttons that used to sit at the top of one /support page:
    // "mine" and "everyone's" are a personal thing and a company thing, which is exactly the
    // split this settings IA already expresses (VRG-221).
    //
    // "My requests", NOT "Support". Both sections had been called Support, which put the same
    // word in the nav twice with nothing to tell them apart — the reader had to infer the scope
    // from which HEADING it sat under, and a label that only means something because of its
    // position is not doing its job. "My requests" says whose they are; Company → Support keeps
    // the generic word, where it is correct (it is the company's support queue).
    label: "My requests",
    // TicketIcon, matching the Help popover's own "My requests" row — same label, same glyph,
    // per §21's rule that one action keeps one icon across surfaces. The life-ring stays with
    // Company → Support, where the label is still "Support".
    icon: <TicketIcon {...ICON_PROPS} />,
    route: ROUTES.SETTINGS_PERSONAL_SUPPORT,
    visible: true,
    component: <SupportPage scope="mine" />,
    // Keeps its own Header, because it owns a primary action ("New request") that the
    // central title cannot carry — same reason Users/Roles/Features/Billing are selfTitled.
    selfTitled: true,
    // Centred readable column, matching Appearance and its Personal siblings: a request card
    // is one paragraph of prose, and at ~1900px the body ran far past a comfortable measure.
    narrow: true,
  },
  {
    // VRG-655 — "Refer & earn" (VRG-680 rename from "Refer a brand"). Every member sees their own
    // referrals and the company totals; `manage_company` adds a Company toggle inside the
    // page. No `permission` here: the page reads on `view_company`, which every company
    // role holds, and reading your own referrals needs nothing more. Internal staff cannot
    // reach it at all (`INTERNAL_ACCOUNT_ROUTES` leaves it out — it is brand-facing).
    label: "Refer & earn",
    // Same glyph as the sidebar's "Refer & earn" row (§21).
    icon: <GiftIcon {...ICON_PROPS} />,
    route: ROUTES.REFERRALS,
    visible: true,
    component: <ReferralsPage />,
    // Keeps its own Header, because it owns a primary action ("Invite a brand").
    selfTitled: true,
  },
];

export const COMPANY_MENU: SettingsMenuItem[] = [
  {
    label: "General",
    // The company itself: name, logos, brand details.
    icon: <Building2Icon {...ICON_PROPS} />,
    route: ROUTES.SETTINGS_COMPANY_GENERAL,
    visible: true,
    component: <CompanyGeneralPage />,
    permission: 'view_company',
    narrow: true,
  },
  // Position 2, directly under General: installing Vera is the first thing a new
  // company does — until a destination is set up, a scanned tag resolves nowhere
  // useful, so every other setting is downstream of this one. It sat at position
  // 7 (after Billing and Address book), which buried the step that has to happen
  // first.
  {
    label: "Installation",
    // Plugging Vera into your Shopify / Webflow / WooCommerce store.
    icon: <PlugIcon {...ICON_PROPS} />,
    route: ROUTES.SETTINGS_COMPANY_INSTALLATION,
    visible: true,
    component: <InstallationPage />,
    permission: 'installation_view',
    narrow: true,
  },
  {
    label: "Users",
    icon: <UsersIcon {...ICON_PROPS} />,
    route: ROUTES.SETTINGS_COMPANY_USERS,
    visible: true,
    component: <UsersPage />,
    selfTitled: true,
    permission: 'users_view',
  },
  {
    label: "Roles & permissions",
    // Who is allowed to do what.
    icon: <ShieldCheckIcon {...ICON_PROPS} />,
    route: ROUTES.SETTINGS_COMPANY_ROLES,
    visible: true,
    component: <RolesPage />,
    selfTitled: true,
    permission: 'view_roles',
  },
    {
    label: "Features",
    // The page is a set of switches (activation triggers, certificate access).
    icon: <ToggleLeftIcon {...ICON_PROPS} />,
    route: ROUTES.SETTINGS_COMPANY_FEATURES,
    visible: true,
    component: <FeaturesPage />,
    // FeaturesPage renders its own <Header> with the single page-level Save/Cancel.
    selfTitled: true,
    // No dedicated feature permission — gate on company visibility.
    permission: 'view_company',
    narrow: true,
  },
  {
    label: "Billing",
    icon: <ReceiptIcon {...ICON_PROPS} />,
    route: ROUTES.SETTINGS_COMPANY_BILLING,
    visible: true,
    component: <BillingPage />,
    selfTitled: true,
    permission: 'billing_view',
  },
  {
    // Everyone at the company's requests — the other half of the pair whose personal side
    // lives under Personal → Support (VRG-221).
    //
    // The `permission` below is the whole reason this is a better home than the in-page
    // toggle it replaces: a user without `support_requests_view_company` now gets a
    // disabled nav row with a tooltip naming the permission (§27), consistent with every
    // other gated section here, instead of a bespoke disabled chip inside a page they had
    // already reached.
    label: "Support",
    icon: <LifeBuoyIcon {...ICON_PROPS} />,
    route: ROUTES.SETTINGS_COMPANY_SUPPORT,
    visible: true,
    component: <SupportPage scope="company" />,
    selfTitled: true,
    narrow: true,
    permission: 'support_requests_view_company',
  },
  {
    label: "Address book",
    icon: <MapPinIcon {...ICON_PROPS} />,
    route: ROUTES.SETTINGS_COMPANY_ADDRESSBOOK,
    visible: true,
    component: <AddressBookPage />,
    permission: 'address_view',
  },
  {
    label: "Integrations",
    icon: <BlocksIcon {...ICON_PROPS} />,
    route: ROUTES.SETTINGS_COMPANY_INTEGRATIONS,
    visible: true,
    component: <IntegrationsPage />,
    permission: 'integration_view',
    term: 'integration',
  },
  {
    // "Fields", not "Field mapping". The section now carries both halves of what a
    // company says about its product fields: Mapping (an integration's fields onto
    // Vera's) and Defaults (the company-wide values a product inherits when it sets
    // none of its own). Mapping REQUIRES a connected integration, so under the old
    // name this row was permanently dead for a company without one — it rendered
    // "No connected store" and nothing else. Defaults needs no integration, so the
    // section is now always useful and Mapping is the conditional half rather than
    // the whole point.
    //
    // The name also has room to grow: `products.custom_fields` already exists in the
    // schema, and custom field DEFINITIONS would belong here rather than under a
    // name that promises only mapping.
    label: "Fields",
    // Their field on the left, ours on the right — still the truest picture of the
    // section, since Defaults is also "a value flowing into a Vera field".
    icon: <ArrowLeftRightIcon {...ICON_PROPS} />,
    route: ROUTES.SETTINGS_COMPANY_FIELDS,
    visible: true,
    component: <FieldsPage />,
    // The page renders its own title, because the title and the Mapping/Defaults toggle are
    // one STICKY block there (`StickyFormHeader`, same as Support). A title left here would
    // sit in the flow and scroll away from the toggle pinned above it.
    selfTitled: true,
    // EITHER view permission opens the page, because the two halves are separately
    // gated and either one on its own is a reason to be here.
    //
    // The single `field_mapping_view` gate this replaces had a specific failure: Mapping
    // needs a connected store, Defaults does not, so a company with no integration was
    // held out of the page by a permission named for the half that could not have done
    // anything for it anyway. That is also why the page lands on Defaults when there is
    // no integration (see FieldsPage) — the gate and the landing tab now agree.
    permission: ['field_mapping_view', 'field_default_view'],
  },
  {
    // VRG-459. Directly after Fields, and the adjacency is the point: a reader who
    // went looking for "where do I tell Vera a number about my business" checks
    // Fields first, finds field mapping and defaults, and the next row is the one
    // they wanted. Not a TAB on Fields, though — Mapping and Defaults are properties
    // of a FIELD, schema-level, and annual return-fraud loss is a company-level
    // business fact. See the route constant and the page header for the full note.
    label: "Business figures",
    // A calculator: these are the numbers Vera computes WITH, not data it holds.
    icon: <CalculatorIcon {...ICON_PROPS} />,
    route: ROUTES.SETTINGS_COMPANY_BUSINESS_FIGURES,
    visible: true,
    component: <BusinessFiguresPage />,
    // The page renders its own title inside a `StickyFormHeader`, so the title and the
    // "N of M supplied" progress line stay pinned together as the figures scroll under
    // them. A title left here would sit in the flow and scroll away from the count.
    // Same arrangement as Fields and Support.
    selfTitled: true,
    // ⚠️ THE VIEW GATE, NOT THE EDIT GATE. Gating this row on `analytics_input_edit`
    // would hide from a reader the figures the numbers they CAN see are calculated
    // from — which makes those numbers unauditable, on a provenance product. The
    // edit permission disables the fields in place, with a tooltip naming it (§27).
    permission: 'view_analytics',
    // Forms and prose, not a table — capped to the readable column like General,
    // Features and Installation.
    narrow: true,
  },
  {
    label: "Activity log",
    icon: <HistoryIcon {...ICON_PROPS} />,
    route: ROUTES.SETTINGS_COMPANY_ACTIVITY,
    visible: true,
    component: <ActivityLogPage />,
    permission: 'activity_log_view',
  },
];

// Resolve which settings section a pathname belongs to by LONGEST route prefix —
// so nested routes (e.g. /settings/billing/invoices or /settings/billing/plan)
// resolve to their section (Billing) instead of falling through to the first
// item (General). Exact match still wins via the longest-prefix rule.
//
// Segment-boundary-aware (`=== route` or `startsWith(route + "/")`), which matters
// more now that the leaves are flat and therefore SHORTER: a bare string prefix would
// make /settings/general an ancestor of a future /settings/general-ledger.
const matchMenuIndex = (
  menu: SettingsMenuItem[],
  pathname: string,
): number => {
  let bestIndex = -1;
  let bestLength = -1;
  menu.forEach((item, index) => {
    const isMatch =
      pathname === item.route || pathname.startsWith(`${item.route}/`);
    if (isMatch && item.route.length > bestLength) {
      bestLength = item.route.length;
      bestIndex = index;
    }
  });
  return bestIndex;
};

/**
 * Which TAB a pathname selects — by menu MEMBERSHIP, not by a path segment.
 *
 * Before VRG-221 this was implicit: `/settings/company/...` contained the word
 * "company", so `Tabs` picked the Company panel off `pathname.includes(tab.value)`
 * without anyone having to decide anything. Flattening the leaves removed that
 * signal — `/settings/users` and `/settings/controls` are indistinguishable as
 * strings — so the scope now comes from the only place it still exists: which of the
 * two menus holds the matched section.
 *
 * Ordering between the two menus is irrelevant because the flat slugs are disjoint —
 * the dev-only collision check below is what keeps that true; the sequence is written
 * company-first only so the more numerous menu short-circuits sooner.
 *
 * Returns null for the LANDING routes, which belong to a scope but to no section —
 * the caller resolves those from the route's own `tab` prop.
 */
const scopeForPathname = (pathname: string): SettingsScope | null => {
  if (matchMenuIndex(COMPANY_MENU, pathname) >= 0) return "company";
  if (matchMenuIndex(PERSONAL_MENU, pathname) >= 0) return "personal";
  return null;
};

/**
 * Dev-only guard on the invariant `scopeForPathname` rests on: with the scope gone
 * from the path, two sections in DIFFERENT menus that flatten to the same slug would
 * be indistinguishable, and the first menu checked would silently swallow the other's
 * URL. That was a real case — both supports wanted `/settings/support` — and it was
 * resolved by naming them after their (already unique) nav labels.
 *
 * A comment saying "keep these unique" is not a guard; adding a section is exactly
 * when nobody re-reads the comment. This reports on the module's first evaluation in
 * dev, so the collision surfaces the moment the section is added rather than as a
 * mis-routed page later.
 *
 * `console.error`, not `throw`: this is module-scope, and a throw here takes the whole
 * app down at import time. The failure it guards against is a mis-routed section, not
 * a corruption — loud is warranted, fatal is not.
 */
if (import.meta.env.DEV) {
  const routes = [...PERSONAL_MENU, ...COMPANY_MENU].map((m) => m.route);
  const duplicates = [...new Set(routes.filter((r, i) => routes.indexOf(r) !== i))];
  if (duplicates.length) {
    console.error(
      `[settings] Sections collide on a flat path: ${duplicates.join(", ")}. ` +
        `The first menu checked will swallow the other's URL. Rename one after its ` +
        `nav label (see ROUTES.SETTINGS_PERSONAL_SUPPORT).`,
    );
  }
}

export const SettingsPage = (props: SettingsPageProps) => {
  const { tab } = props;
  const location = useLocation();
  const scrollableDivRef = useRef<HTMLDivElement>(null);
  // Drives the scroll-collapse of the Personal/Company tab bar: once the page is
  // scrolled at all, the full-width tab bar (and its bottom divider) animates left
  // down to the tabs' own width, freeing the right-hand space so the Billing content
  // scrolls up past the pinned bar.
  const [scrolled, setScrolled] = useState(false);
  /**
   * Will the page header carry a "← Back to <referrer>" link on this visit?
   *
   * The back link's OWN predicate, so the header's height and its contents are decided by one rule
   * (the same reason `Header` uses it). It answers false on the common path — landing here from the
   * sidebar, with no referrer in router state — and true only when a cross-surface link brought the
   * user in (Studio → Features, an invoice's payment-method cell → Payment methods, `?` → Controls).
   *
   * Everything on this page that is positioned "below the header" has to move when it does.
   */
  const hasReferrerBack = useHasReferrerBack();
  const settingsHeaderH =
    SETTINGS_HEADER_H + (hasReferrerBack ? SETTINGS_BACK_LINK_ROW_H : 0);
  const settingsBarsH =
    SETTINGS_BARS_H + (hasReferrerBack ? SETTINGS_BACK_LINK_ROW_H : 0);
  const settingsSubnavTop =
    SETTINGS_SUBNAV_TOP + (hasReferrerBack ? SETTINGS_BACK_LINK_ROW_H : 0);
  const [selectedPersonal, setSelectedPersonal] = useState(() => {
    const index = matchMenuIndex(PERSONAL_MENU, location.pathname);
    return index >= 0 ? PERSONAL_MENU[index] : PERSONAL_MENU[0];
  });
  const [selectedCompany, setSelectedCompany] = useState(() => {
    const index = matchMenuIndex(COMPANY_MENU, location.pathname);
    return index >= 0 ? COMPANY_MENU[index] : COMPANY_MENU[0];
  });
  const navigate = useNavigate();

  /**
   * VRG-395 — internal Vera staff get an ACCOUNT page, not a brand's Settings.
   *
   * They have no company row, so every COMPANY section is either empty or silently
   * scoped to whichever company happens to resolve. That is a correctness problem
   * rather than a tidiness one, and the permission system cannot express it: an
   * internal role legitimately HOLDS `view_company`, `billing_view` and the rest, so
   * every gate above answers "yes" for a user with nothing for them to apply to.
   *
   * HIDDEN, not disabled (§27/§46). A locked-with-tooltip company nav would be
   * telling a Vera admin to ask an admin for a permission they already hold, to reach
   * a company they do not have. These are whole SURFACES they cannot reach, which is
   * exactly the case §27 says to hide; the disable-with-tooltip treatment stays for
   * the company user who is missing one section's permission.
   *
   * Filtered from the SAME allow-list the route guard uses (`INTERNAL_ACCOUNT_ROUTES`),
   * so the nav and the URL cannot disagree about what exists.
   */
  const isInternal = useIsInternalUser();
  // VRG-680 — the Refer & earn entry exists only while the API's launch switch is on
  // (`GET /features`; off by default, and off until it answers). Hidden, not disabled
  // (§27): no permission or admin can bring it back, so a disabled row would promise access
  // nobody can grant. The route itself redirects in App.tsx (`RequireReferralsEnabled`).
  const { enabled: referralsLive } = useReferralsEnabled();
  const personalMenu: SettingsMenuItem[] = (
    isInternal
      ? PERSONAL_MENU.filter((m) => INTERNAL_ACCOUNT_ROUTES.includes(m.route))
      : PERSONAL_MENU
  ).filter((m) => referralsLive || m.route !== ROUTES.REFERRALS);
  // Annotated, not inferred: `isInternal ? [] : COMPANY_MENU` infers
  // `SettingsMenuItem[] | never[]`, and calling `.filter` on that union in
  // `sectionedNav` is not callable under TS's union-of-signatures rule.
  const companyMenu: SettingsMenuItem[] = isInternal ? [] : COMPANY_MENU;

  /**
   * VRG-227 — a SINGLE-SEAT company (Starter, Individual: `seats: 1` in the Stripe
   * catalog seed) collapses Settings to ONE list with no Personal/Company pre-headers,
   * and hides Users, Roles & permissions, and the company Activity log — see
   * `SINGLE_SEAT_HIDDEN_ROUTES`'s docblock for why those three specifically.
   *
   * Detected by SEAT LIMIT, not plan name — plans get renamed, seats are the fact that
   * matters (`useCompanySeats` / `SeatUsage`, which documents `limit: null` as
   * "unlimited or unsynced"). `limit === 1` EXACTLY; `null` and any limit > 1 fall
   * through to the normal split — fail toward showing MORE, matching the direction
   * `SeatUsage` itself takes for a company that is (or looks) over/unlimited.
   *
   * Disabled for internal users (no company row to look up), and while the query is
   * loading `seats` is `null` (see the hook), so `seats?.limit === 1` reads `false` and
   * this renders the NORMAL split until the real answer arrives — the same fail-open
   * direction, applied to "unknown" as well as "unlimited". No separate loading branch
   * is needed: the one comparison already degrades to the safe default.
   */
  const { seats } = useCompanySeats({ enabled: !isInternal });
  const isSingleSeat = !isInternal && seats?.limit === 1;
  const singleSeatMenu: SettingsMenuItem[] | null = isSingleSeat
    ? [
        ...personalMenu,
        ...companyMenu.filter((m) => !SINGLE_SEAT_HIDDEN_ROUTES.includes(m.route)),
      ]
    : null;

  /**
   * Which panel renders. DERIVED here and pushed into `Tabs` as a controlled value,
   * rather than left for `Tabs` to infer from the pathname.
   *
   * `Tabs`' own inference is `pathname.includes(tab.value)` against the tab values
   * `/settings/personal` and `/settings/company`. With the scope out of the path that
   * matches NOTHING for every leaf, and its no-match branch falls back to `tabs[0]` —
   * i.e. every company section would have rendered inside the Personal panel. That is
   * the same class of failure as the stale-value bug fixed in `Tabs` (0c1295403), so
   * it is fixed the same way: one source of truth for the active value, computed from
   * the state that actually determines it, with the component controlled rather than
   * guessing.
   *
   * Section first, route prop second: a section always knows its own menu, and the
   * prop only has to answer for the landing routes, which have no section.
   */
  const scope: SettingsScope =
    scopeForPathname(location.pathname) ?? tab ?? "personal";
  const activeTabValue =
    scope === "company" ? ROUTES.SETTINGS_COMPANY : ROUTES.SETTINGS_PERSONAL;

  const onHandleSelectPersonal = (item: any) => {
    setSelectedPersonal(item);
    navigate(item.route);
  };

  const onHandleSelectCompany = (item: any) => {
    setSelectedCompany(item);
    navigate(item.route);
  };

  // §27: gate each company-settings section by its view permission. useCanAccess is
  // space-aware — for an in-space Vera staffer it reflects the brand's grant, so the
  // sections the grant doesn't cover disable here. Hooks are called unconditionally
  // in a fixed order (one per distinct permission).
  const canAccess: Partial<Record<Permissions, boolean>> = {
    view_company: useCanAccess({ action: 'view_company' }).isHasAccess,
    users_view: useCanAccess({ action: 'users_view' }).isHasAccess,
    view_roles: useCanAccess({ action: 'view_roles' }).isHasAccess,
    billing_view: useCanAccess({ action: 'billing_view' }).isHasAccess,
    address_view: useCanAccess({ action: 'address_view' }).isHasAccess,
    installation_view: useCanAccess({ action: 'installation_view' }).isHasAccess,
    integration_view: useCanAccess({ action: 'integration_view' }).isHasAccess,
    field_mapping_view: useCanAccess({ action: 'field_mapping_view' }).isHasAccess,
    field_default_view: useCanAccess({ action: 'field_default_view' }).isHasAccess,
    activity_log_view: useCanAccess({ action: 'activity_log_view' }).isHasAccess,
  };
  // An array `permission` is ANY-of, so the section is locked only when EVERY listed
  // permission is known-false. `=== false` (not `!`) throughout, because `undefined`
  // means "not one of the permissions resolved above" and must not lock a row —
  // `support_requests_view_company` relies on that today.
  const requiredPermissions = (item: SettingsMenuItem): Permissions[] =>
    item.permission === undefined
      ? []
      : Array.isArray(item.permission)
        ? item.permission
        : [item.permission];
  const isSectionLocked = (item: SettingsMenuItem) => {
    const required = requiredPermissions(item);
    return required.length > 0 && required.every((p) => canAccess[p] === false);
  };
  // §27: names the SPECIFIC missing permission (via SECTION_PERMISSION_LABELS) —
  // the previous copy here ("You don't have permission to view X") named the
  // section, not the permission, which isn't actionable on its own.
  //
  // For an any-of section, name every alternative joined by "or": the row is locked
  // because the user holds NONE of them, so any single one is a valid thing to ask for
  // and naming just the first would send them to ask for the wrong half.
  const getLockedTooltip = (item: SettingsMenuItem): string | undefined => {
    const labels = requiredPermissions(item)
      .map((p) => SECTION_PERMISSION_LABELS[p])
      .filter((l): l is string => !!l);
    if (labels.length === 0) return undefined;
    const named =
      labels.length === 1 ? `the '${labels[0]}' permission` : `the ${labels.map((l) => `'${l}'`).join(' or ')} permission`;
    return `You need ${named}. Ask an admin to grant it.`;
  };

  // Keep the selected sub-page in sync with the URL so external/in-app
  // navigation (e.g. the assign-role modal's "Create custom role" link routing
  // to /settings/roles) actually switches the visible panel — not just
  // the address bar. Without this, the selection is only set on left-nav click.
  useEffect(() => {
    const personalIdx = matchMenuIndex(PERSONAL_MENU, location.pathname);
    if (personalIdx >= 0) {
      setSelectedPersonal((prev) => (prev.route === PERSONAL_MENU[personalIdx].route ? prev : PERSONAL_MENU[personalIdx]));
      return;
    }
    const companyIdx = matchMenuIndex(COMPANY_MENU, location.pathname);
    if (companyIdx >= 0) {
      setSelectedCompany((prev) => (prev.route === COMPANY_MENU[companyIdx].route ? prev : COMPANY_MENU[companyIdx]));
    }
  }, [location.pathname]);

  // Toggle the scroll-collapse once the page leaves the top. Boolean (not raw
  // scrollTop) so we only re-render on the 0↔scrolled crossing, not every pixel.
  useEffect(() => {
    const div = scrollableDivRef.current;
    if (!div) return;
    const handleScroll = () => setScrolled(div.scrollTop > 0);
    div.addEventListener("scroll", handleScroll, { passive: true });
    return () => div.removeEventListener("scroll", handleScroll);
  }, []);
  // F TRIAL — one sectioned rail listing every destination, rendered in BOTH panels so the nav is
  // identical whichever route you are on. 48px rows, no continuous rule, brand marker on the
  // selected row only.
  //
  // Duplicating it across the two panels is the trial's shortcut: the real change merges the panels
  // into one. Selecting a Company item from the Personal panel routes to a company page, which swaps
  // the panel — and because both render the same nav, that reads as a single list.
  //
  // It WILL scroll: 15 rows at 48px plus section chrome is ~792px against ~647px of column. That was
  // accepted to see how the taller rows feel.
  //
  // ⚠️ THE WRAPPER BOX IS FUNCTIONAL, NOT DECORATIVE — do not flatten it back to a
  // fragment. It carries `settingsNavIconMotionSx` (per-icon hover motion, shared with
  // the sidebar rail — see components/navigation/navIconMotion.ts), which has to be
  // applied ONCE at a container rather than per row: `SideNav` is a @repo/ui component
  // that knows nothing about Vera's product vocabulary, and threading a motion prop
  // through it would put the same decision in two places and leave the third — the next
  // nav to adopt icons — silently un-animated.
  //
  // Scoped HERE and not on the page, because the motion keys off `.MuiMenuItem-root` and
  // every Select dropdown in the app renders MenuItems. Most portal to the body and would
  // escape a page-level scope anyway, but "most" is not a reason to widen it.
  //
  // It adds a block-level element with no styles of its own between the sticky column and
  // the two section Boxes, so it cannot move anything: measured before and after, the
  // first nav row sits at y=114 in both the company and internal compositions, which is
  // the invariant the `visibility: hidden` heading spacer below exists to hold.
  // VRG-227 — a single-seat company collapses to ONE combined group (see
  // `singleSeatMenu` above), rather than two groups where one happens to be empty:
  // the two differ in the `pt: heading === "Personal" ? 0 : 5` gap below, which must
  // NOT appear between, say, Notifications (personal) and General (company) once
  // they read as one continuous list.
  const navSections: ReadonlyArray<readonly [string, SettingsMenuItem[]]> =
    singleSeatMenu
      ? [["Personal", singleSeatMenu]]
      : [
          ["Personal", personalMenu],
          ["Company", companyMenu],
        ];
  const sectionedNav = (currentRoute: string) => (
    <Box
      sx={[settingsNavIconMotionSx, selectedNavIconSx(NAV_ROW_MENU_ITEM)]}
      data-tour="settings-nav"
    >
      {navSections.map(([heading, menu]) => {
        const shown = menu.filter((m) => m.visible !== false);
        // An empty section must not render its heading — a user without company permissions would
        // otherwise get a "Company" label over nothing.
        if (!shown.length) return null;
        // …and a SOLE section must not SHOW its heading either, which is the same rule one
        // step further on. For an internal user the Company half is gone, so "PERSONAL" would
        // be a group label with nothing to distinguish it FROM, sitting under a page already
        // titled "Account". It carries information only while there are two groups to tell
        // apart. A company user always has both, so this branch was internal-only — until
        // VRG-227's single-seat mode, which also collapses to one group (`navSections` above
        // is a single tuple there, not two with an empty second half).
        //
        // ⚠️ HIDDEN, NOT REMOVED — the heading's BOX is load-bearing and this is not obvious.
        //
        // This nav pins at SETTINGS_BARS_H (64) below the scroll container's padding box, i.e.
        // viewport 88. The sticky page-title band above it ends at 92.5. So the nav column has
        // ALWAYS started 4.5px inside the band — the same class of stale-constant drift the
        // SETTINGS_HEADER_H comment above documents (measured: header height 73, minus its own
        // -4.5 lift, is 68.5 of occupied space against a constant of 64).
        //
        // That was invisible only because the thing being clipped was this heading, whose text
        // sits below the cut. Drop it and the first INTERACTIVE row inherits the position — its
        // rounded corner and selected-state highlight then render under the band. Measured on
        // the first pass: first row top 88 vs band bottom 92.5, a -4.5px overlap.
        //
        // So the element stays and only its ink is removed. `visibility: hidden` keeps the box
        // and drops it from the a11y tree, so no screen reader announces a group label that is
        // not there. The invariant this buys: THE FIRST NAV ROW SITS AT THE SAME y WHETHER OR
        // NOT A HEADING RENDERS (measured 114 in both views) — which is stronger than clearing
        // the band, and it cannot drift, because the spacer IS the heading. A hand-set `pt` of
        // "about a heading's worth" would go stale the first time the label's font or padding
        // moved, and this file already carries three separate post-mortems of exactly that.
        //
        // Do NOT "fix" this by raising the sticky `top`: that offset is shared with the company
        // view, where the geometry is right, and moving it would drag a correct layout to
        // repair an incorrect one.
        const soleSection = isInternal || isSingleSeat;
        return (
          <Box key={heading}>
            <Typography
              variant="body4"
              color="text.secondary"
              sx={{
                display: "block",
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                pl: 3,
                pb: 2,
                pt: heading === "Personal" ? 0 : 5,
                // See `soleSection` above — the box is kept, the ink is not. Every other
                // property is left untouched so the reserved space is exactly the space a
                // rendered heading would occupy.
                ...(soleSection ? { visibility: "hidden" } : {}),
              }}
            >
              {heading}
            </Typography>
            {/* PILL, not rail. Rounding F's rows to match the app (buttons/fields/sidebar rows are
                8px, cards 12px — F's 0px was the only square element on the page) collapses into the
                pill: a 2px left marker on an 8px-radius row follows the curve and becomes a bent
                sliver, and a continuous rule runs behind the rounded corners leaving gaps at each
                one. The marker/rule idiom depends on square edges.
                So the rounded sectioned nav IS sections + pills — same 48px rows, filled brand tint
                at navSelectedSubtle, no marker, no rule. Consistent with the sidebar's own rows. */}
            <SideNav
              items={shown.map((item) => {
                // §27 access gating, carried over from the Company nav this replaces: a section the
                // user cannot view stays VISIBLE but disabled, with a tooltip naming the permission.
                // Dropping this would have made locked sections silently clickable.
                const locked = isSectionLocked(item);
                return {
                  label: item.label,
                  icon: item.icon,
                  value: item.route,
                  visible: item.visible,
                  disabled: locked,
                  tooltip: locked ? getLockedTooltip(item) : undefined,
                };
              })}
              value={currentRoute}
              onChange={(route) => {
                const p = PERSONAL_MENU.find((m) => m.route === route);
                if (p) return onHandleSelectPersonal(p);
                const c = COMPANY_MENU.find((m) => m.route === route);
                if (c) return onHandleSelectCompany(c);
              }}
            />
          </Box>
        );
      })}
    </Box>
  );


  return (
    <Box
      style={{
        height: "100%",
        overflowY: "auto",
        // Clip horizontal overflow so the page can't scroll sideways into empty
        // space on small screens (the horizontal sub-nav scrolls internally).
        overflowX: "hidden",
        msOverflowStyle: "none",
        scrollbarWidth: "none",
      }}
      ref={scrollableDivRef}
    >
      <Header
        // "Account" for internal staff, and deliberately not "Profile": the page would then
        // be a Profile containing a section called Profile, a name colliding with its own
        // child. "Account" is the conventional bucket for exactly this set (GitHub, Stripe,
        // Linear all use it), and it stays honest when the page is only ever the four
        // personal sections. A company user keeps "Settings" — the page is still both halves
        // for them.
        title={isInternal ? "Account" : "Settings"}
        // `hideReferrerBack={!hasReferrerBack}` — i.e. render the wayfinding row ONLY when a link
        // will actually occupy it, never as reserved empty space.
        //
        // It used to be an unconditional `hideReferrerBack`, justified as "the outer header defers
        // to the per-page header". That deferral had no target: every section under /settings
        // renders its inner header with `nested`, and `nested` suppresses the wayfinding row
        // entirely — so the link fell through the gap and NO settings page could ever show one,
        // including the ~8 call sites that already hand this page a referrer (Studio's white-label
        // card, the activation-trigger link, the payment-method and address cells, …).
        //
        // The flag stays for the empty case rather than being dropped outright, because Header
        // compensates for an empty row with a -24.5px lift that THIS page cannot receive: its sx is
        // spread last and replaces `position: relative` with `sticky`, which re-purposes `top` as a
        // pin offset. Not rendering the empty row keeps the common path (arriving from the sidebar)
        // byte-identical to before — measured, the title stays at 28.4px.
        hideReferrerBack={!hasReferrerBack}
        // `top: -4.5`, not 0, and a matching negative marginTop.
        //
        // This page's sticky wrapper is spread into the Header's sx LAST, so it overrides the
        // `position: relative` + `top` the Header uses to line the title up with the sidebar's
        // brand mark — which is why every other page aligned and this one stayed 4.5px low. The
        // sticky offset has to carry the shift itself here.
        //
        // Both values, because they do different jobs: `marginTop` moves the element's FLOW
        // position (what you see at rest), `top` moves where it PINS (what you see scrolled). With
        // only one of them the title would sit at 48.5 at rest and jump to 44 on first scroll —
        // the same pinned-vs-flow mismatch that made the docs rails lurch.
        //
        // BOTH GO TO 0 when a back link renders. The lift exists to close an empty wayfinding row;
        // with a link in it the row is real content and the header should sit at its natural top,
        // exactly as it does on every other page that shows one (measured on /support/requests:
        // link top 32, title top 70.9). Keeping -4.5 here would put the link 4.5px higher than the
        // same link everywhere else.
        // `marginBottom: 0` — CANCELS a compensation that belongs to a mechanism this page
        // does not use. This is the whole of the 4.5px overlap fix (VRG-438); everything else
        // is re-deriving the constants that were calibrated against the broken geometry.
        //
        // THE BUG. `Header` lifts a title onto the sidebar's brand-mark line with a MATCHED
        // PAIR: `position: relative` + `top: -4.5px` moves the PAINTED box without touching
        // flow, and `marginBottom: -4.5px` then reclaims the 4.5px the painted box vacated so
        // the content below stays flush with it. Neither half is correct alone.
        //
        // This page breaks the pair. Its sx is spread LAST, so `position` becomes `sticky` —
        // which re-purposes `top` as a pin offset — and the lift has to move to `marginTop`
        // instead. But `marginTop` shifts the FLOW box, not just the painted one, so the
        // reclamation has already happened by the time `Header`'s `marginBottom` applies it a
        // SECOND time. One lift, two compensations, and the second one has nothing left to
        // reclaim, so it eats live layout.
        //
        // MEASURED at 1280x800 on /settings/users, before: header painted [19.5, 92.5], next
        // sibling top 88 — i.e. the content column started 4.5px inside an OPAQUE band at
        // zIndex 3, so `elementFromPoint` at each action button's top edge returned the
        // "Settings" header rather than the button. After: next sibling top 92.5, flush.
        //
        // It affected EVERY /settings/* section, not just Users — this Header is rendered once
        // by the shell for all of them. It was invisible until an action row happened to sit
        // at the top of a section, because the only other thing at that y was the sub-nav's
        // first heading, whose ink sits below the cut (see the `visibility: hidden` spacer in
        // `sectionedNav` — that spacer's OTHER justification, holding the first nav row at a
        // constant y, still stands, so it is left alone).
        //
        // Explicit rather than omitted: `Header` sets `marginBottom` from its own lift branch,
        // so leaving it out inherits -4.5px. It is 0 in BOTH branches — with a back link the
        // Header takes no lift at all, so 0 is already what it computes there.
        sx={{
          position: "sticky",
          top: hasReferrerBack ? 0 : "-4.5px",
          marginTop: hasReferrerBack ? 0 : "-4.5px",
          marginBottom: 0,
          zIndex: 3,
          backgroundColor: "background.default",
        }}
      />
      <Tabs
        redirect={false}
        // Controlled — see `activeTabValue` above. The bar itself is `display: none`
        // under the F trial, so `onChange` cannot fire from a click today; it is wired
        // anyway so un-hiding the bar restores a working control rather than an inert
        // one (a controlled Tabs with no onChange is a tab strip you cannot use).
        value={activeTabValue}
        onChange={(value) => navigate(value)}
        sx={{
          // F TRIAL: hidden, not removed. The Tabs component still drives which TabPanel renders
          // from the route, so unmounting it would mean restructuring both panels — which is the
          // real F implementation, not a trial. `display: none` gets the sectioned look now and is
          // one line to revert.
          display: "none",
          position: "sticky",
          top: settingsHeaderH,
          zIndex: 2,
          backgroundColor: "background.default",
          // Scroll-collapse: at the top the tab bar (and its full-width bottom
          // divider) spans the page; once scrolled it animates down to the tabs' own
          // width (max-content) so the divider slides left and the Billing content
          // scrolls up past the bar. Mobile stays full-width (opaque bar covers the row).
          maxWidth: { xs: "100%", md: scrolled ? "max-content" : "100%" },
          transition: "max-width 500ms ease-in-out",
        }}
        tabs={[
          {
            label: "Personal",
            value: ROUTES.SETTINGS_PERSONAL,
          },
          {
            label: "Company",
            value: ROUTES.SETTINGS_COMPANY,
          },
        ]}
      >
        <TabPanel value={ROUTES.SETTINGS_PERSONAL} sx={{ p: 0, pt: 0 }}>
          {/* Mobile: horizontal scrollable sub-nav, sticky just under the tabs.
              zIndex 2 keeps its opaque background above the page content (zIndex 1)
              so content scrolls cleanly underneath instead of bleeding through. */}
          <Box
            sx={{
              display: { xs: "block", md: "none" },
              position: "sticky",
              // Pin the sub-nav right at the tab bar's sticky bottom: Header (top:0,
              // SETTINGS_HEADER_H) + the Personal/Company tab bar (SETTINGS_TABBAR_H). The TabPanel's
              // mobile pt is 0 (below), so the pills keep the same margin to the tab
              // bar stuck as un-stuck — no jump on scroll. The ::before extends an
              // opaque strip UP into the tab bar to close any sub-pixel gap that would
              // otherwise let page content bleed through between the two; the tab bar
              // (zIndex:2 > this Box's zIndex:1) paints over it, so its underline still
              // shows. The borderBottom is the divider under the nav row.
              top: settingsBarsH,
              zIndex: 1,
              backgroundColor: "background.default",
              pt: 3,
              pb: 1.5,
              mb: 2,
              borderBottom: "1px solid",
              borderColor: "divider",
              "&::before": {
                content: '""',
                position: "absolute",
                left: 0,
                right: 0,
                top: -12,
                height: 14,
                backgroundColor: "background.default",
              },
            }}
          >
            {/* §27: this horizontal (narrow-viewport) rail used to render `PERSONAL_MENU`
                without the `locked`/tooltip treatment the vertical `sectionedNav` rail
                applies below — so a locked section stayed clickable on a narrow viewport.
                Reuses `isSectionLocked`/`getLockedTooltip` rather than re-deriving them. */}
            {/* `personalMenu`, not `PERSONAL_MENU` — the narrow-viewport rail has to carry the
                internal reduction too, or an internal user gets the full Personal list back
                the moment the viewport drops below `md` (the same class of miss as the §27
                gating that this rail was missing before). */}
            {/* VRG-227: `singleSeatMenu ?? personalMenu` — a single-seat company's merged
                list has to appear here too, or the mobile rail would show a "Personal only"
                list while the desktop `sectionedNav` (below) shows the real merged one. The
                `onChange` below checks both raw menus, so a company item in this list still
                routes correctly. */}
            <SideNav
              direction="horizontal"
              items={(singleSeatMenu ?? personalMenu).map((item) => {
                const locked = isSectionLocked(item);
                return {
                  label: item.label,
                  icon: item.icon,
                  value: item.route,
                  visible: item.visible,
                  disabled: locked,
                  tooltip: locked ? getLockedTooltip(item) : undefined,
                };
              })}
              value={selectedPersonal.route}
              onChange={(route) => {
                const p = PERSONAL_MENU.find((m) => m.route === route);
                if (p) return onHandleSelectPersonal(p);
                const c = COMPANY_MENU.find((m) => m.route === route);
                if (c) return onHandleSelectCompany(c);
              }}
            />
          </Box>
          <Grid container>
            <Grid item md={2} sx={{ display: { xs: "none", md: "block" } }}>
              <Box
                sx={{
                  position: "sticky",
                  top: settingsSubnavTop,
                  // No negative margin. The `mt: -3` here existed purely to cancel the TabPanel's
                  // `pt: 6`; with that padding removed it pulled the first section heading UP out of
                  // this box, and since the box now scrolls (`overflow-y: auto`) the heading was
                  // clipped rather than merely shifted.
                  // The sectioned nav is ~822px and the column slot is ~647px at 1280x800, so it
                  // has to scroll. It scrolls ITSELF rather than the page: start position stays
                  // pinned where it is, and the list is cut off 24px above the viewport bottom —
                  // the same gutter the page keeps on its other three sides (§22), so the cut edge
                  // lines up with everything else instead of running to the screen edge.
                  //
                  // Page-level scrolling was the alternative and is worse here: the content column
                  // is usually shorter than the nav, so the whole page would scroll to reveal nav
                  // items and drag the content up with it.
                  // `- 48px`, not `- 24px`: the box's STICKY top is SETTINGS_SUBNAV_TOP but its
                  // FLOW position is 24px lower (the TabPanel's pt:6 less this box's mt:-3), and
                  // unstuck is the normal state here because the nav is the tallest thing on the
                  // page so there is little page scroll to stick it. Measured with `- 24px`: the box
                  // ended at exactly 800 on an 800px viewport — flush to the edge, no gutter.
                  //
                  // So the extra 24 accounts for the flow offset, giving exactly the 24px gutter
                  // unstuck; when it does stick, the gap grows to 48px rather than shrinking to 0.
                  // Erring toward a larger gap is the right way round — the list never runs off the
                  // screen edge.
                  // Height budgeted from the box's real TOP IN THE VIEWPORT — the page gutter plus
                  // the bars above it — then the same 24px gutter again at the bottom (§22). Using
                  // the sticky `top` alone was wrong twice before, because that value is relative to
                  // the scroll container's padding box while the measurement that matters is against
                  // the viewport. Now that pinned == flow, one expression is correct in both states.
                  maxHeight: `calc(100vh - ${
                    SETTINGS_PAGE_GUTTER + settingsSubnavTop
                  }px - ${SETTINGS_PAGE_GUTTER}px)`,
                  overflowY: "auto",
                  // No horizontal scrollbar from the rows' own padding.
                  overflowX: "hidden",
                  // Keep the scrollbar out of the layout until it is needed.
                  scrollbarGutter: "stable",
                }}
              >
                {sectionedNav(selectedPersonal.route)}
              </Box>
            </Grid>
            <Grid
              item
              xs={12}
              md={10}
              p={0}
              pl={{ xs: 0, md: 6 }}
              sx={{ zIndex: 0, position: "relative" }}
            >
              {/* Same readable-measure cap as the Company panel. */}
              <Box sx={selectedPersonal.narrow ? { maxWidth: NARROW_COLUMN_WIDTH, mx: 'auto' } : undefined}>
                {!selectedPersonal.selfTitled && (
                  <Header nested title={selectedPersonal.label} />
                )}
                {/* Sections render inside the provider so a sticky section header
                    (My requests) knows how tall the shell header above it is — a
                    height that CHANGES with the back-link row. */}
                <SettingsStickyOffsetContext.Provider value={settingsHeaderH}>
                  {selectedPersonal.component}
                </SettingsStickyOffsetContext.Provider>
              </Box>
            </Grid>
          </Grid>
        </TabPanel>
        <TabPanel value={ROUTES.SETTINGS_COMPANY} sx={{ p: 0, pt: 0 }}>
          {/* Mobile: horizontal scrollable sub-nav, sticky just under the tabs.
              zIndex 2 keeps its opaque background above the page content (zIndex 1)
              so content scrolls cleanly underneath instead of bleeding through. */}
          <Box
            sx={{
              display: { xs: "block", md: "none" },
              position: "sticky",
              // Pin the sub-nav right at the tab bar's sticky bottom: Header (top:0,
              // SETTINGS_HEADER_H) + the Personal/Company tab bar (SETTINGS_TABBAR_H). The TabPanel's
              // mobile pt is 0 (below), so the pills keep the same margin to the tab
              // bar stuck as un-stuck — no jump on scroll. The ::before extends an
              // opaque strip UP into the tab bar to close any sub-pixel gap that would
              // otherwise let page content bleed through between the two; the tab bar
              // (zIndex:2 > this Box's zIndex:1) paints over it, so its underline still
              // shows. The borderBottom is the divider under the nav row.
              top: settingsBarsH,
              zIndex: 1,
              backgroundColor: "background.default",
              pt: 3,
              pb: 1.5,
              mb: 2,
              borderBottom: "1px solid",
              borderColor: "divider",
              "&::before": {
                content: '""',
                position: "absolute",
                left: 0,
                right: 0,
                top: -12,
                height: 14,
                backgroundColor: "background.default",
              },
            }}
          >
            {/* §27: same gap as the Personal horizontal rail above — this narrow-viewport
                rail rendered COMPANY_MENU with no `locked`/tooltip treatment at all, so a
                locked company section stayed clickable on a narrow viewport. */}
            {/* VRG-227: `singleSeatMenu ?? COMPANY_MENU` — same merge as the Personal
                rail above, so whichever TabPanel happens to be active on mobile shows
                the same single combined list a single-seat company sees on desktop. */}
            <SideNav
              direction="horizontal"
              items={(singleSeatMenu ?? COMPANY_MENU).map((item) => {
                const locked = isSectionLocked(item);
                return {
                  label: item.label,
                  icon: item.icon,
                  value: item.route,
                  visible: item.visible,
                  disabled: locked,
                  tooltip: locked ? getLockedTooltip(item) : undefined,
                };
              })}
              value={selectedCompany.route}
              onChange={(route) => {
                const p = PERSONAL_MENU.find((m) => m.route === route);
                if (p) return onHandleSelectPersonal(p);
                const c = COMPANY_MENU.find((m) => m.route === route);
                if (c) return onHandleSelectCompany(c);
              }}
            />
          </Box>
          <Grid container>
            <Grid item md={2} sx={{ display: { xs: "none", md: "block" } }}>
              <Box
                sx={{
                  position: "sticky",
                  top: settingsSubnavTop,
                  // No negative margin. The `mt: -3` here existed purely to cancel the TabPanel's
                  // `pt: 6`; with that padding removed it pulled the first section heading UP out of
                  // this box, and since the box now scrolls (`overflow-y: auto`) the heading was
                  // clipped rather than merely shifted.
                  // The sectioned nav is ~822px and the column slot is ~647px at 1280x800, so it
                  // has to scroll. It scrolls ITSELF rather than the page: start position stays
                  // pinned where it is, and the list is cut off 24px above the viewport bottom —
                  // the same gutter the page keeps on its other three sides (§22), so the cut edge
                  // lines up with everything else instead of running to the screen edge.
                  //
                  // Page-level scrolling was the alternative and is worse here: the content column
                  // is usually shorter than the nav, so the whole page would scroll to reveal nav
                  // items and drag the content up with it.
                  // `- 48px`, not `- 24px`: the box's STICKY top is SETTINGS_SUBNAV_TOP but its
                  // FLOW position is 24px lower (the TabPanel's pt:6 less this box's mt:-3), and
                  // unstuck is the normal state here because the nav is the tallest thing on the
                  // page so there is little page scroll to stick it. Measured with `- 24px`: the box
                  // ended at exactly 800 on an 800px viewport — flush to the edge, no gutter.
                  //
                  // So the extra 24 accounts for the flow offset, giving exactly the 24px gutter
                  // unstuck; when it does stick, the gap grows to 48px rather than shrinking to 0.
                  // Erring toward a larger gap is the right way round — the list never runs off the
                  // screen edge.
                  // Height budgeted from the box's real TOP IN THE VIEWPORT — the page gutter plus
                  // the bars above it — then the same 24px gutter again at the bottom (§22). Using
                  // the sticky `top` alone was wrong twice before, because that value is relative to
                  // the scroll container's padding box while the measurement that matters is against
                  // the viewport. Now that pinned == flow, one expression is correct in both states.
                  maxHeight: `calc(100vh - ${
                    SETTINGS_PAGE_GUTTER + settingsSubnavTop
                  }px - ${SETTINGS_PAGE_GUTTER}px)`,
                  overflowY: "auto",
                  // No horizontal scrollbar from the rows' own padding.
                  overflowX: "hidden",
                  // Keep the scrollbar out of the layout until it is needed.
                  scrollbarGutter: "stable",
                }}
              >
                {sectionedNav(selectedCompany.route)}
              </Box>
            </Grid>
            <Grid
              item
              xs={12}
              md={10}
              p={0}
              pl={{ xs: 0, md: 6 }}
              sx={{ zIndex: 0, position: "relative" }}
            >
              {/* Readable-measure cap for form/prose sections (see `narrow` on
                  SettingsMenuItem). Wraps the Header too, so the title stays
                  aligned with the content it belongs to rather than sitting off
                  to one side of a narrower body. */}
              <Box sx={selectedCompany.narrow ? { maxWidth: NARROW_COLUMN_WIDTH, mx: 'auto' } : undefined}>
                {!selectedCompany.selfTitled && (
                  <Header
                    nested
                    title={selectedCompany.label}
                    titleAdornment={
                      selectedCompany.term
                        ? <InfoTooltip term={selectedCompany.term} />
                        : undefined
                    }
                  />
                )}
                {/* Sections render inside the provider so a sticky section header
                    (My requests) knows how tall the shell header above it is — a
                    height that CHANGES with the back-link row. */}
                <SettingsStickyOffsetContext.Provider value={settingsHeaderH}>
                  {selectedCompany.component}
                </SettingsStickyOffsetContext.Provider>
              </Box>
            </Grid>
          </Grid>
        </TabPanel>
      </Tabs>
    </Box>
  );
};
